// Barrel export for the 10 bundled standard TTF fonts. Each import
// resolves to a string URL (Vite `?url`-style asset import via *.ttf
// module declaration in packages/renderer/src/global.d.ts).
//
// Consumer (RendererWebGL) uses these URLs to load Troika's SDF atlases
// via `preloadFont({ font: <url> })` and as `cFont` assignments that feed
// `new Text({ font: <url> })`.

import fontItalianno from "./standard-fonts/Italianno-Regular.ttf";
import fontJetBrains from "./standard-fonts/JetBrainsMono-Variable.ttf";
import fontMerriweather from "./standard-fonts/Merriweather-Variable.ttf";
import fontMerriweatherSans from "./standard-fonts/MerriweatherSans-Variable.ttf";
import fontNotoSans from "./standard-fonts/NotoSans-VariableFont.ttf";
import fontNotoSerif from "./standard-fonts/NotoSerif-VariableFont.ttf";
import fontPortLligatSlab from "./standard-fonts/PortLligatSlab-Regular.ttf";
import fontRoboto from "./standard-fonts/RobotoCondensed-Variable.ttf";
import fontSourceSans3 from "./standard-fonts/SourceSans3-Variable.ttf";
import fontSourceSerif4 from "./standard-fonts/SourceSerif4-Variable.ttf";
// Roboto (echtes Roboto, nicht Condensed — fontRoboto oben ist historisch
// falsch benannt) + Barlow Semi Condensed: dieselben .woff-Dateien wie in
// Jans Original-FONTS-Map (Troika/Typr liest woff direkt).
import fontRobotoRegular from "./Roboto/roboto-v29-latin-regular.woff";
// Roboto Bold/Black (Jans Auftrag 07.09.2026): Der Startbildschirm setzt den
// Titel in Schnitt 800 — mit nur dem Regular-Schnitt fettete der Browser
// kuenstlich (verschmierter Titel, Editor wie Viewer). Latin-Subset wie das
// Regular (google-webfonts-helper, Roboto v51, Apache 2.0).
import fontRobotoBold from "./Roboto/roboto-v51-latin-700.woff";
import fontRobotoBlack from "./Roboto/roboto-v51-latin-900.woff";
import fontBarlowSemiCondensed from "./barlow-semi-condensed/barlow-semi-condensed-latin-400-normal.woff";
import fontBarlowSemiCondensedBold from "./barlow-semi-condensed/barlow-semi-condensed-latin-700-normal.woff";
import fontBarlowSemiCondensedBlack from "./barlow-semi-condensed/barlow-semi-condensed-latin-900-normal.woff";
import fontSofiaSansSemiCondensed from "./SofiaSansSemiCondensed/SofiaSansSemiCondensed-VariableFont_wght.ttf";

export {
    fontItalianno,
    fontJetBrains,
    fontMerriweather,
    fontMerriweatherSans,
    fontNotoSans,
    fontNotoSerif,
    fontPortLligatSlab,
    fontRoboto,
    fontSourceSans3,
    fontSourceSerif4,
    fontRobotoRegular,
    fontRobotoBold,
    fontRobotoBlack,
    fontBarlowSemiCondensed,
    fontBarlowSemiCondensedBold,
    fontBarlowSemiCondensedBlack,
};

/**
 * SCHNITTE (Jan 06.10.2026): Das TEXT-Asset bekommt ein Feld „Schnitt“, und
 * die Schriften des Template-Editors kommen dazu. WebGL-Text (Troika) braucht
 * je Schnitt eine eigene Datei (ttf/otf/woff, kein woff2, keine variablen
 * Achsen). Die Schnitte liegen als Latin-Woff aus den Fontsource-Paketen
 * (OFL 1.1, Lizenz je Ordner) unter `schnitte/<paket>/` und werden hier
 * eingesammelt — Light 300 bis Black 900, soweit vorhanden.
 *
 * Bei den bisherigen Familien bleibt der Normalschnitt die bisherige Datei:
 * Alt-Scrollys zeichnen exakt wie vorher, die Schnitte kommen nur dazu.
 */
const SCHNITT_DATEIEN = import.meta.glob<string>("./schnitte/*/*-latin-*-normal.woff", {
    eager: true,
    query: "?url",
    import: "default",
});

/** Gewicht → Datei aus `schnitte/<ordner>/<ordner>-latin-<gewicht>-normal.woff`. */
function schnitteAusOrdner(ordner: string): Record<number, string> {
    const out: Record<number, string> = {};
    const praefix = `./schnitte/${ordner}/${ordner}-latin-`;
    for (const [pfad, url] of Object.entries(SCHNITT_DATEIEN)) {
        if (!pfad.startsWith(praefix)) continue;
        const gewicht = Number(pfad.slice(praefix.length).split("-")[0]);
        if (Number.isFinite(gewicht)) out[gewicht] = url;
    }
    return out;
}

export interface SchriftFamilie {
    /** Beschriftung im Auswahlfeld */
    label: string;
    /** Gewicht (300 … 900) → Datei */
    schnitte: Readonly<Record<number, string>>;
}

/** Familie bauen; `vorhanden` (bisherige Dateien) geht vor den Ordner-Schnitten. */
const familie = (label: string, ordner: string | null, vorhanden: Record<number, string> = {}): SchriftFamilie => ({
    label,
    schnitte: { ...(ordner ? schnitteAusOrdner(ordner) : {}), ...vorhanden },
});

/** Die neuen Familien (06.10.2026): Template-Editor-Schriften, Barlow Condensed, freie Doppelgänger. */
const NEUE_FAMILIEN = {
    BarlowCondensed: familie("Barlow Condensed", "barlow-condensed"),
    BebasNeue: familie("Bebas Neue", "bebas-neue"),
    DMSans: familie("DM Sans", "dm-sans"),
    Inter: familie("Inter", "inter"),
    Lora: familie("Lora", "lora"),
    Montserrat: familie("Montserrat", "montserrat"),
    Nunito: familie("Nunito", "nunito"),
    OpenSans: familie("Open Sans", "open-sans"),
    Oswald: familie("Oswald", "oswald"),
    PlayfairDisplay: familie("Playfair Display", "playfair-display"),
    Poppins: familie("Poppins", "poppins"),
    WorkSans: familie("Work Sans", "work-sans"),
    // Arial, Helvetica, Times New Roman und Georgia dürfen wir nicht
    // mitliefern (gekaufte Systemschriften) — freie, gleich breite Doppelgänger.
    Arimo: familie("Arimo ≈ Arial", "arimo"),
    Tinos: familie("Tinos ≈ Times New Roman", "tinos"),
    Gelasio: familie("Gelasio ≈ Georgia", "gelasio"),
};

/**
 * Alle Standard-Familien mit ihren Schnitten — EINE Liste für beide Editoren
 * (TEXT-Asset, Boxen, CTA) und beide Renderer (Pro-Editor, Viewer). Die
 * Schlüssel sind die gespeicherten `family`-Werte.
 */
export const SCHRIFT_FAMILIEN: Readonly<Record<string, SchriftFamilie>> = {
    BarlowSemiCondensed: familie("Barlow Semi Condensed", "barlow-semi-condensed", {
        400: fontBarlowSemiCondensed,
        700: fontBarlowSemiCondensedBold,
        900: fontBarlowSemiCondensedBlack,
    }),
    Italianno: familie("Italianno", null, { 400: fontItalianno }),
    JetBrainsMono: familie("JetBrains Mono", "jetbrains-mono", { 400: fontJetBrains }),
    Merriweather: familie("Merriweather", "merriweather", { 400: fontMerriweather }),
    MerriweatherSans: familie("Merriweather Sans", "merriweather-sans", { 400: fontMerriweatherSans }),
    NotoSans: familie("Noto Sans", "noto-sans", { 400: fontNotoSans }),
    NotoSerif: familie("Noto Serif", "noto-serif", { 400: fontNotoSerif }),
    PortLligatSlab: familie("Port Lligat Slab", null, { 400: fontPortLligatSlab }),
    Roboto: familie("Roboto", "roboto", { 400: fontRobotoRegular, 700: fontRobotoBold, 900: fontRobotoBlack }),
    RobotoCondensed: familie("Roboto Condensed", "roboto-condensed", { 400: fontRoboto }),
    SofiaSansSemiCondensed: familie("Sofia Sans SemiCondensed", "sofia-sans-semi-condensed", {
        400: fontSofiaSansSemiCondensed,
    }),
    SourceSans3: familie("Source Sans 3", "source-sans-3", { 400: fontSourceSans3 }),
    SourceSerif4: familie("Source Serif 4", "source-serif-4", { 400: fontSourceSerif4 }),
    ...NEUE_FAMILIEN,
};

/** Typografische Namen der Schnitte (sprachneutral, wie in Figma/InDesign). */
export const SCHNITT_NAMEN: Readonly<Record<number, string>> = {
    100: "Thin",
    200: "ExtraLight",
    300: "Light",
    400: "Regular",
    500: "Medium",
    600: "SemiBold",
    700: "Bold",
    800: "ExtraBold",
    900: "Black",
};

/** Vorhandene Schnitte einer Familie, aufsteigend; leer für unbekannte Familien. */
export function schriftSchnitte(familieName: string | undefined): number[] {
    const f = familieName ? SCHRIFT_FAMILIEN[familieName] : undefined;
    return f ? Object.keys(f.schnitte).map(Number).sort((a, b) => a - b) : [];
}

/**
 * Nächstgelegener vorhandener Schnitt. Gleich weit weg: ab 500 der fettere,
 * darunter der leichtere (wie die Schriftwahl im Browser).
 */
export function naechsterSchnitt(vorhanden: readonly number[], wunsch: number): number | undefined {
    let beste: number | undefined;
    for (const g of vorhanden) {
        if (beste === undefined) {
            beste = g;
            continue;
        }
        const d = Math.abs(g - wunsch);
        const db = Math.abs(beste - wunsch);
        if (d < db || (d === db && (wunsch >= 500 ? g > beste : g < beste))) beste = g;
    }
    return beste;
}

/**
 * Datei für Familie + Schnitt (fehlt der Schnitt: Normalschnitt 400 bzw. der
 * nächste). `undefined` für Familien außerhalb der Standardliste (Team-,
 * Kunden-, Alt-Schriften) — dann gilt die bisherige Auflösung.
 */
export function schriftDatei(familieName: string | undefined, gewicht?: number): string | undefined {
    const f = familieName ? SCHRIFT_FAMILIEN[familieName] : undefined;
    if (!f) return undefined;
    const g = naechsterSchnitt(schriftSchnitte(familieName), gewicht ?? 400);
    return g === undefined ? undefined : f.schnitte[g];
}

const regulaer = (f: SchriftFamilie): string => {
    const g = naechsterSchnitt(Object.keys(f.schnitte).map(Number), 400);
    return g === undefined ? "" : f.schnitte[g];
};

/**
 * ZUSATZ-SCHNITTE für HTML-Text (Jans Schriftwahl mit zwei Spalten, 06.10.2026):
 * Die bisherigen Familien behalten ihre eine Webschrift-Regel (variable Datei
 * bzw. Normalschnitt, ohne Stärke-Angabe) UNVERÄNDERT; dahinter kommen die
 * übrigen Schnitte als eigene Regeln mit `font-weight`. Gemessen in Chrome
 * (06.10.2026): Sofia 800 bzw. Noto 700 zeichnen mit der Schnitt-Datei exakt
 * wie bisher aus der variablen Datei (Breite ±0,1 px, gleiche Tinte) — für
 * bestehende Scrollys ändert sich nichts, Light/SemiBold usw. werden echt.
 * Roboto und die neuen Familien stehen schon vollständig in STANDARD_FONT_WEIGHTS.
 */
export const ZUSATZ_SCHNITTE: Readonly<Record<string, ReadonlyArray<{ weight: number; url: string }>>> =
    Object.fromEntries(
        Object.entries(SCHRIFT_FAMILIEN)
            .filter(([k]) => !(k in NEUE_FAMILIEN))
            .map(([k, f]) => {
                // Roboto hat 400/700/900 schon als vollständige Regeln (s. unten)
                const schonDa = k === "Roboto" ? [400, 700, 900] : [400];
                return [
                    k,
                    Object.keys(f.schnitte)
                        .map(Number)
                        .filter((w) => !schonDa.includes(w))
                        .sort((a, b) => a - b)
                        .map((weight) => ({ weight, url: f.schnitte[weight] })),
                ];
            })
            .filter(([, liste]) => (liste as unknown[]).length > 0)
    );

/**
 * GOOGLE-NAMEN (Jans Entscheidung 06.10.2026): Template-Editor, Branding und
 * der Google-Lader des Viewers führen manche Schriften unter ihrem Google-
 * Namen („Open Sans“), das Register unter einem Schlüssel ohne Leerzeichen
 * („OpenSans“). Google liefert dem Viewer nur den Normalschnitt — fette
 * Überschriften waren künstlich gefettet. Unter dem Google-Namen kommen deshalb
 * unsere Dateien mit ALLEN Schnitten dazu (gleiche Schrift, echte Schnitte).
 * Gleichnamige (Inter, Montserrat, Oswald …) brauchen keinen Eintrag.
 */
export const GOOGLE_NAMEN: Readonly<Record<string, string>> = {
    BebasNeue: "Bebas Neue",
    DMSans: "DM Sans",
    OpenSans: "Open Sans",
    PlayfairDisplay: "Playfair Display",
    SourceSerif4: "Source Serif 4",
    WorkSans: "Work Sans",
};

/** Webschrift-Regeln unter den Google-Namen: dieselben Dateien, jeder Schnitt mit Stärke. */
export function googleNamenRegeln(): string[] {
    return Object.entries(GOOGLE_NAMEN).flatMap(([key, name]) => {
        const f = SCHRIFT_FAMILIEN[key];
        if (!f) return [];
        return Object.keys(f.schnitte)
            .map(Number)
            .sort((a, b) => a - b)
            .map(
                (w) =>
                    `@font-face { font-family: '${name}'; src: url('${f.schnitte[w]}'); font-weight: ${w}; font-display: swap; }`
            );
    });
}

/**
 * Keyed map of the bundled standard fonts to their resolved URL strings.
 * Keys match the `content.family` values the Pro-Editor writes
 * (STANDARD_FONTS in app/src/proeditor/.../standardFonts.ts).
 * Die bisherigen Einträge sind unverändert; die neuen Familien (06.10.2026)
 * kommen mit ihrem Normalschnitt dazu.
 */
const BISHERIGE_STANDARD_FONTS = {
    Italianno: fontItalianno,
    JetBrainsMono: fontJetBrains,
    Merriweather: fontMerriweather,
    MerriweatherSans: fontMerriweatherSans,
    NotoSans: fontNotoSans,
    NotoSerif: fontNotoSerif,
    PortLligatSlab: fontPortLligatSlab,
    Roboto: fontRobotoRegular,
    RobotoCondensed: fontRoboto,
    SourceSans3: fontSourceSans3,
    SourceSerif4: fontSourceSerif4,
    BarlowSemiCondensed: fontBarlowSemiCondensed,
} as const;

export const STANDARD_FONTS: typeof BISHERIGE_STANDARD_FONTS & Readonly<Record<string, string>> = {
    ...BISHERIGE_STANDARD_FONTS,
    ...Object.fromEntries(Object.entries(NEUE_FAMILIEN).map(([k, f]) => [k, regulaer(f)])),
};

/**
 * Zusatz-Schnitte pro Familie (font-weight -> Datei). Wer @font-face fuer eine
 * Familie schreibt, MUSS diese Liste mit abrollen (readerStandardFonts, Editor-
 * standardFonts, Viewer-SSR cover-fonts) — sonst faellt der Browser bei
 * fettem Text auf kuenstliches Fetten zurueck. Familien ohne Eintrag haben nur
 * die eine Datei aus STANDARD_FONTS (variable Fonts tragen ihre Gewichte selbst).
 *
 * Bewusst NUR Roboto (wie bisher) + die neuen Familien: Die Webschrift-Regeln
 * der bisherigen Familien (Sofia in allen Template-Titeln!) bleiben exakt wie
 * sie waren — sonst sähen veröffentlichte HTML-Texte plötzlich anders aus.
 * Deren neue Schnitte wirken im WebGL-Text (schriftDatei).
 */
export const STANDARD_FONT_WEIGHTS: Readonly<
    Record<string, ReadonlyArray<{ weight: number; url: string }>>
> = {
    Roboto: [
        { weight: 400, url: fontRobotoRegular },
        { weight: 700, url: fontRobotoBold },
        { weight: 900, url: fontRobotoBlack },
    ],
    ...Object.fromEntries(
        Object.entries(NEUE_FAMILIEN).map(([k, f]) => [
            k,
            Object.keys(f.schnitte)
                .map(Number)
                .sort((a, b) => a - b)
                .map((weight) => ({ weight, url: f.schnitte[weight] })),
        ])
    ),
};

/**
 * Eine Webschrift-Regel für einen Schnitt einer Team-/Kundenschrift. Mit
 * Stärke (und Kursiv), wenn bekannt — sonst wie bisher ohne (Alt-Einträge in
 * fontInfos, die nur Familie + Datei kennen). Jans Schriftwahl 06.10.2026:
 * mehrere hochgeladene Schnitte einer Familie zeichnen so jeder für sich.
 */
export function teamSchriftRegel(f: { familyName: string; fontUrl: string; weight?: number; italic?: boolean }): string {
    const gewicht = typeof f.weight === "number" && f.weight > 0 ? ` font-weight: ${f.weight};` : "";
    const stil = f.italic ? " font-style: italic;" : "";
    return `@font-face { font-family: '${f.familyName}'; src: url('${f.fontUrl}');${gewicht}${stil} font-display: swap; }`;
}

type TeamSchnittInfo = { familyName?: string; fontUrl?: string; weight?: number; italic?: boolean };

/**
 * Datei einer Team-/Kundenschrift aus den hinterlegten fontInfos: der aufrechte
 * Schnitt, der `gewicht` am nächsten liegt (Einträge ohne Stärke zählen als
 * 400). Seit der Schriftwahl mit zwei Spalten (06.10.2026) kann eine Familie
 * mit mehreren Schnitten hinterlegt sein — vorher gab es je Familie eine
 * Datei, und die Suchen nahmen einfach die erste bzw. letzte Fundstelle.
 */
export function teamSchnittDatei(
    infos: ReadonlyArray<TeamSchnittInfo | null | undefined> | null | undefined,
    familie: string | undefined,
    gewicht?: number
): string | null {
    if (!familie || !Array.isArray(infos)) return null;
    const treffer = infos.filter((f): f is TeamSchnittInfo => !!f && f.familyName === familie && !!f.fontUrl);
    if (treffer.length === 0) return null;
    const aufrecht = treffer.filter((f) => !f.italic);
    const kandidaten = aufrecht.length > 0 ? aufrecht : treffer;
    const staerke = (f: TeamSchnittInfo) => (typeof f.weight === "number" && f.weight > 0 ? f.weight : 400);
    const ziel = naechsterSchnitt(kandidaten.map(staerke), gewicht ?? 400);
    return (kandidaten.find((f) => staerke(f) === ziel) ?? kandidaten[0]).fontUrl ?? null;
}
